import classNames from "classnames";
import { useActivityPermissions, useActivityTemplateType } from "PFApp/activities/hooks";
import { BookingDetailActivity } from "PFApp/booking/components/booking_detail_activity";
import { DetailsPanelSource } from "PFApp/booking/components/details_panel";
import { useRoleDetailsPanelOpenAllocateManually } from "PFApp/booking/components/details_panel/hooks";
import css from "PFApp/booking/parts/overview/detail_view/detail_view.module.scss";
import { useBookingActivityContext } from "PFApp/booking/parts/providers/booking_activity_context_provider";
import { RollForwardInfoContent } from "PFApp/components/rollforward/roll_forward_info_content";
import useBookingModulePermissions from "PFApp/use_booking_module_permissions";
import { Stack } from "PFComponents/containers";
import { Divider } from "PFComponents/divider";
import { LoadingDots } from "PFComponents/loading_dots";
import { Typography } from "PFComponents/typography";
import { getProfileName } from "PFCore/helpers/profile";
import useBookingCategories from "PFCore/hooks/use_booking_categories";
import { Activity } from "PFTypes";
import { Dispatch, SetStateAction, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { H5 } from "../parts/h5";
import { Label } from "../parts/label";
import { Participants } from "../parts/participants";
import { Buttons } from "./buttons";
import { CustomFields } from "./custom_fields";
import { Details } from "./details";
import { Duration } from "./duration";
import { Skills } from "./skills";

type RoleDetailContentProps = RoleDetailProps & {
  activity: Activity;
  parentActivity?: Activity;
  isParentActivityFetching: boolean;
  isParentActivityError: boolean;
};

const RoleDetailContent = ({
  setAllocateManually,
  source,
  activity,
  parentActivity,
  isParentActivityFetching,
  isParentActivityError
}: RoleDetailContentProps) => {
  const { isEnabled: isBMEnabled } = useBookingModulePermissions();
  const openAllocateManually = useRoleDetailsPanelOpenAllocateManually();

  const { t } = useTranslation();
  const { checkWriteAccessToAtLeastOneCategory } = useBookingCategories();
  const hasWriteAccessToAtLeastOneCategory = checkWriteAccessToAtLeastOneCategory({ demandRelated: true });
  const { canSeeShortlist } = useActivityPermissions(activity);

  const { isAudit, isRole } = useActivityTemplateType(activity);
  const isAuditRole = isAudit && isRole;
  const canAllocateManually =
    isBMEnabled && canSeeShortlist && !isAuditRole && hasWriteAccessToAtLeastOneCategory;

  useEffect(() => {
    if (!openAllocateManually) {
      return;
    }
    if (canAllocateManually) {
      setAllocateManually(!isAuditRole);
    }
  }, [openAllocateManually, isAuditRole, hasWriteAccessToAtLeastOneCategory, canAllocateManually]);

  if (!activity) {
    return null;
  }

  return (
    <>
      <Stack className={classNames(css.root, css.roleDetail)} gap="spacingLg">
        <Participants activity={activity} />
        <Divider />
        <div className={css.item}>
          <H5>{t("description")}</H5>
          <Typography variant="bodyRegular">{activity.description}</Typography>
        </div>
        <RollForwardInfoContent activity={activity} isRole={true} mode="link" isVertical />
        {parentActivity && !isParentActivityError && (
          <>
            <Divider />
            <div className={css.item}>
              <BookingDetailActivity
                activity={parentActivity}
                isLoading={isParentActivityFetching}
                source={source}
              />
            </div>
            <Divider />
          </>
        )}
        {!!activity.metadata.availability && <Duration availability={activity.metadata.availability} />}
        <Details activity={activity} />
        <Divider />
        {canAllocateManually && (
          <>
            <Buttons setAllocateManually={setAllocateManually} />
            <Divider />
          </>
        )}
        <Skills activity={activity} />
        <CustomFields activity={activity} />
        <Divider />
        <div className={css.item}>
          <Label text={t("creator")} />
          <Typography variant="bodyBold">{getProfileName(activity.profile)}</Typography>
        </div>
      </Stack>
    </>
  );
};

type RoleDetailProps = {
  setAllocateManually: Dispatch<SetStateAction<boolean>>;
  source?: DetailsPanelSource;
};

export const RoleDetail = ({ setAllocateManually, source }: RoleDetailProps) => {
  const { activity, parentActivity, isParentActivityFetching, isParentActivityError } =
    useBookingActivityContext();

  if (!activity) {
    return <LoadingDots />;
  }

  return (
    <RoleDetailContent
      setAllocateManually={setAllocateManually}
      source={source}
      activity={activity}
      parentActivity={parentActivity}
      isParentActivityFetching={isParentActivityFetching}
      isParentActivityError={isParentActivityError}
    />
  );
};
